import { Namespace, TFunction } from 'react-i18next';
import { AirbnbCancellationPolicy } from 'models/AirbnbCancellationPolicy';
import { normalizeKey } from 'utils/localeUtils';

export const KNOWN_CANCELLATION_POLICY_ORDER: string[] = [
  AirbnbCancellationPolicy.FLEXIBLE,
  AirbnbCancellationPolicy.MODERATE,
  AirbnbCancellationPolicy.BETTER_STRICT_WITH_GRACE_PERIOD,
  AirbnbCancellationPolicy.FIRM_14,
  AirbnbCancellationPolicy.STRICT_14_WITH_GRACE_PERIOD,
  AirbnbCancellationPolicy.STRICT,
  AirbnbCancellationPolicy.SUPER_STRICT_30,
  AirbnbCancellationPolicy.SUPER_STRICT_60,
];

export const isKnownAirbnbCancellationPolicy = (value: string): boolean => {
  return (Object.values(AirbnbCancellationPolicy) as string[]).includes(value);
};

/**
 * Resolves a display label for an Airbnb cancellation policy value.
 *
 * Known values resolve to their translated name. Unknown values are humanized: underscores are
 * replaced with spaces — e.g. `super_strict_30_new` -> `super strict 30 new`.
 */
export const getAirbnbCancellationPolicyLabel = (
  value: string,
  t: TFunction<Namespace<'en'>>,
): string => {
  if (isKnownAirbnbCancellationPolicy(value)) {
    return t(
      normalizeKey(
        `pageProperty.mainSettings.airbnbSettings.cancellationPolicyOptions.${value}`,
      ),
    ) as string;
  }

  return value.replace(/_/g, ' ');
};

/**
 * Returns the full display catalogue of Airbnb cancellation policies as select options,
 * in the canonical display order. Use when no `eligibleCancellationPolicies` list is
 * available (e.g. seasonal rules span multiple properties).
 */
export const getKnownAirbnbCancellationPolicyOptions = (
  t: TFunction<Namespace<'en'>>,
): { value: string; label: string }[] =>
  KNOWN_CANCELLATION_POLICY_ORDER.map((value) => ({
    value,
    label: getAirbnbCancellationPolicyLabel(value, t),
  }));

/**
 * Catalogue values that have a short refund-terms description to show under their label.
 *
 * `strict` is deliberately excluded: it is part of the known display catalogue (see
 * `isKnownAirbnbCancellationPolicy`), but has no refund-terms copy defined, so inventing terms
 * for it would show a PM incorrect rules. "Known" and "has a description" are therefore not the
 * same predicate.
 */
const CANCELLATION_POLICIES_WITH_DESCRIPTION: string[] = [
  AirbnbCancellationPolicy.FLEXIBLE,
  AirbnbCancellationPolicy.MODERATE,
  AirbnbCancellationPolicy.BETTER_STRICT_WITH_GRACE_PERIOD,
  AirbnbCancellationPolicy.FIRM_14,
  AirbnbCancellationPolicy.STRICT_14_WITH_GRACE_PERIOD,
  AirbnbCancellationPolicy.SUPER_STRICT_30,
  AirbnbCancellationPolicy.SUPER_STRICT_60,
];

/**
 * Resolves a short refund-terms description for an Airbnb cancellation policy value, for display
 * under its label in the option list.
 *
 * Returns `undefined` for any value without a description — this includes `strict` (known, but
 * deliberately has no description) and any unknown/synthetic value — rather than falling back to
 * `t(...)`, which would render a raw i18n key.
 */
export const getAirbnbCancellationPolicyDescription = (
  value: string,
  t: TFunction<Namespace<'en'>>,
): string | undefined => {
  if (!CANCELLATION_POLICIES_WITH_DESCRIPTION.includes(value)) {
    return undefined;
  }

  return t(
    normalizeKey(
      `pageProperty.mainSettings.airbnbSettings.cancellationPolicyDescriptions.${value}`,
    ),
  ) as string;
};
